import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useDateFilter } from "@/context/DateFilterContext";
import { LayoutDashboard, Target, FlaskConical, Trophy, Stethoscope, Archive, LogOut, Calendar, Sparkles, Menu, BookMarked, Settings as SettingsIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/goals", label: "Goals & OKRs", icon: Target },
  { to: "/pipeline", label: "R&D Pipeline", icon: FlaskConical },
  { to: "/benchmarks", label: "Benchmark Products", icon: Trophy },
  { to: "/services", label: "Services & Feedback", icon: Stethoscope },
  { to: "/research", label: "Research Topics", icon: BookMarked },
  { to: "/archived", label: "Archived / Ignored", icon: Archive },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

const RANGES = [
  { k: "today", l: "Today" }, { k: "month", l: "This Month" }, { k: "quarter", l: "This Quarter" },
  { k: "last_quarter", l: "Last Quarter" }, { k: "ytd", l: "YTD" }, { k: "all", l: "All Time" },
];

function NavList({ onNavigate }) {
  return (
    <nav className="flex-1 px-3 py-4 space-y-1">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate}
          data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-accent text-accent-foreground" : "text-slate-600 hover:bg-slate-100"
            }`}>
          <n.icon size={18} /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2">
      <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-white"><Sparkles size={18} /></div>
      <div className="leading-tight">
        <p className="font-head font-extrabold text-sm text-slate-900">Youngness · Awish</p>
        <p className="text-[11px] text-muted-foreground">R&D Innovation Hub</p>
      </div>
    </div>
  );
}

function UserFooter({ user, logout }) {
  return (
    <div className="border-t border-slate-200 p-3">
      <div className="flex items-center justify-between rounded-lg px-2 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {user?.picture
            ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" referrerPolicy="no-referrer" />
            : <div className="grid h-8 w-8 place-items-center rounded-full bg-primary/10 text-xs font-bold text-primary">{(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}</div>}
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-800">{user?.name}</p>
            <p className="truncate text-[11px] text-muted-foreground">{user?.email}</p>
          </div>
        </div>
        <button onClick={logout} className="text-slate-400 hover:text-red-500" data-testid="logout-btn"><LogOut size={18} /></button>
      </div>
    </div>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { rangeKey, setRangeKey, custom, setCustom } = useDateFilter();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden md:flex w-64 flex-col border-r border-slate-200 bg-white sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-slate-200"><Brand /></div>
        <NavList />
        <UserFooter user={user} logout={logout} />
      </aside>

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 flex flex-wrap items-center gap-3 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl px-4 py-3 md:px-5">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden rounded-lg border border-slate-200 p-2 text-slate-600" data-testid="mobile-menu-btn"><Menu size={18} /></button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 flex flex-col">
              <div className="px-5 py-5 border-b border-slate-200"><Brand /></div>
              <NavList onNavigate={() => setMobileOpen(false)} />
              <UserFooter user={user} logout={logout} />
            </SheetContent>
          </Sheet>

          <Calendar size={18} className="text-primary hidden sm:block" />
          <div className="flex flex-wrap gap-1" data-testid="date-filter-bar">
            {RANGES.map((r) => (
              <button key={r.k} onClick={() => setRangeKey(r.k)} data-testid={`date-filter-${r.k}`}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  rangeKey === r.k ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{r.l}</button>
            ))}
            <button onClick={() => setRangeKey("custom")} data-testid="date-filter-custom"
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                rangeKey === "custom" ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>Custom</button>
          </div>
          {rangeKey === "custom" && (
            <div className="flex items-center gap-2">
              <Input type="date" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="h-8 w-36 text-xs" data-testid="custom-start" />
              <span className="text-xs text-muted-foreground">to</span>
              <Input type="date" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="h-8 w-36 text-xs" data-testid="custom-end" />
            </div>
          )}
        </header>
        <main className="p-5 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
